import axios from 'axios'

/**
 * The per-field reasons behind a schema-level refusal, keyed by field.
 *
 * A field the request schema rejects — blank, over-long — comes back in the
 * shared validation shape, `{"detail": [{loc, msg}]}` with no `code`, which
 * `readRefusal` reads as "not one of ours" and discards. That shape is the only
 * one naming *which* field is at fault, so it is read here rather than collapsed
 * into a message about the whole box.
 *
 * `loc` is `["body", "<field>"]`. `fields` is the set the caller has an input
 * for; anything else is dropped, since there is nowhere to show it.
 */
export const readFieldErrors = <Field extends string>(
  error: unknown,
  fields: readonly Field[]
): Partial<Record<Field, string>> => {
  if (!axios.isAxiosError(error)) {
    return {}
  }
  const body: unknown = error.response?.data
  if (typeof body !== 'object' || body === null) {
    return {}
  }
  const detail = (body as { detail?: unknown }).detail
  if (!Array.isArray(detail)) {
    return {}
  }

  const wanted = new Set<string>(fields)
  const byField: Partial<Record<Field, string>> = {}
  for (const entry of detail) {
    if (typeof entry !== 'object' || entry === null) {
      continue
    }
    const { loc, msg } = entry as { loc?: unknown; msg?: unknown }
    if (!Array.isArray(loc) || typeof msg !== 'string') {
      continue
    }
    const field = loc[loc.length - 1]
    if (typeof field !== 'string' || !wanted.has(field)) {
      continue
    }
    // First reason per field: the box shows one message under each input.
    byField[field as Field] ??= msg
  }
  return byField
}
